const COOKIE_MAX_AGE = 34560000 // 400 days
const TEST_COOKIE_NAME = '__anagram_domain_test'

// undefined = not yet probed, null = probed and failed, string = discovered domain
let cachedParentDomain: string | null | undefined

function getCookieValue(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))
  return match ? (match[1] ?? null) : null
}

function deleteCookie(name: string, domain: string): void {
  // biome-ignore lint/suspicious/noDocumentCookie: cookie utility must use document.cookie
  document.cookie = `${name}=; domain=${domain}; path=/; max-age=0`
}

/**
 * Discovers the registrable domain (eTLD+1) by probing for the broadest
 * domain scope the browser will accept a cookie on. Browsers reject cookies
 * scoped to a public suffix (`.com`, `.co.uk`, …), so the first scope that
 * "sticks" is the registrable domain. This avoids needing a public suffix
 * list and handles multi-part TLDs like `.co.uk` and `.com.au`.
 */
export function discoverParentDomain(): string | null {
  if (cachedParentDomain !== undefined) return cachedParentDomain

  const hostname = window.location.hostname
  if (!hostname || hostname === 'localhost') {
    cachedParentDomain = null
    return null
  }

  const parts = hostname.split('.')
  if (parts.length < 2) {
    cachedParentDomain = null
    return null
  }

  // Probe from the broadest legal scope downward. For "shop.example.co.uk"
  // we try ".co.uk" (browser rejects, public suffix), then ".example.co.uk"
  // (accepted, registrable domain), and stop there.
  for (let i = parts.length - 2; i >= 0; i--) {
    const candidate = `.${parts.slice(i).join('.')}`
    // biome-ignore lint/suspicious/noDocumentCookie: cookie utility must use document.cookie
    document.cookie = `${TEST_COOKIE_NAME}=1; domain=${candidate}; path=/; SameSite=Lax; Secure`

    if (getCookieValue(TEST_COOKIE_NAME) === '1') {
      deleteCookie(TEST_COOKIE_NAME, candidate)
      cachedParentDomain = candidate
      return cachedParentDomain
    }
  }

  cachedParentDomain = null
  return null
}

/**
 * Sets a cookie scoped to the parent domain with the required attributes:
 * `path=/`, `SameSite=Lax`, `Secure`, `max-age=34560000` (400 days).
 * Values are URI-encoded to handle JSON and special characters.
 */
export function setCookie(key: string, value: unknown): void {
  try {
    const domain = discoverParentDomain()
    if (!domain) return

    const raw = typeof value === 'string' ? value : JSON.stringify(value)
    const encoded = encodeURIComponent(raw)

    // biome-ignore lint/suspicious/noDocumentCookie: cookie utility must use document.cookie
    document.cookie = `${key}=${encoded}; domain=${domain}; path=/; SameSite=Lax; Secure; max-age=${COOKIE_MAX_AGE}`
  } catch {
    // Silently ignore — cookie writes must never break the SDK
  }
}

/**
 * Reads a cookie written by `setCookie`. URI-decodes the value and attempts
 * to JSON-parse it; falls back to the raw decoded string when parse fails.
 * Returns `null` when the cookie is absent or unreadable.
 */
export function getCookie(key: string): unknown {
  try {
    const raw = getCookieValue(key)
    if (raw == null) return null
    const decoded = decodeURIComponent(raw)
    try {
      return JSON.parse(decoded)
    } catch {
      return decoded
    }
  } catch {
    return null
  }
}

/** Exposed for testing — resets the cached parent domain. */
export function _resetCachedDomain(): void {
  cachedParentDomain = undefined
}
